import React, { useEffect, useState } from "react";
import { Input, Select, message, Spin } from "antd";
import dayjs from "dayjs";
import { SetHeading } from "../../context/NavbarContext";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DateFilter from "../ui/DateFilter";
import MoneyTransferTable from "./accountingTables/moneyTransferTable";
import EditMoneyModal from "./editMoneyModal";
import {
  createMoneyTransfer,
  editOrDeleteMoneyTransfer,
  getMoneyTransfers,
  getTransferBalance,
} from "../../API  Functions/accountingFunctions";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import i18n from "../../i18n";
import { getSubstores } from "../../API  Functions/ProfileFunctions";
import LoadingSpinner from "../Loading";
import { URLS } from "../../API/API endpoints";
import axiosInstance from "../../AxiosInstance";
import { MultiSelectBox } from "../CustomWidgets/SelectBox";

const { Option } = Select;

const MoneyTransfer = () => {
  const { t } = useTranslation();
  const storeId = useSelector(
    (state) => state?.login?.user?.result?.company_id,
  );
  const { masterStore } = useSelector((state) => state?.login);

  const [fromType, setFromType] = useState("");
  const [toType, setToType] = useState("");
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [selectedRange, setSelectedRange] = useState("thisMonth");
  const [loading, setLoading] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const [currentLimit, setCurrentLimit] = useState(10);
  const [currentOffset, setCurrentOffset] = useState(0);

  const today = dayjs();
  const formatToday = today.format("YYYY-MM-DD");
  const startOfMonth = today.startOf("month").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(startOfMonth);
  const [endDate, setEndDate] = useState(formatToday);
  const [transferFromBanks, setTransferFromBanks] = useState([]);
  const [transferToBanks, setTransferToBanks] = useState([]);

  const [selected, setSelected] = useState("Cash to Bank");

  const [subStores, setSubStores] = useState([]);
  const [fromStore, setFromStore] = useState(null);
  const [toStore, setToStore] = useState(null);

  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  console.log("fromStoreE-------->", fromStore);

  useEffect(() => {
    if (storeId) {
      getSubStoresData();
      getTransferFromBank([storeId]);
    }
  }, [storeId]);

  const getTransferFromBank = async (id) => {
    try {
      const response = await axiosInstance.post(URLS.TRANSFER_FROM_BANKS, {
        company_id: id,
      });
      if (response?.data?.result?.status_code == 200) {
        setTransferFromBanks(response?.data?.result?.data);
      } else {
        setTransferFromBanks([]);
      }
    } catch (err) {
      console.error("Failed to fetch substores:", err);
    }
  };

  const getTransferToBank = async (id) => {
    try {
      const response = await axiosInstance.post(URLS.TRANSFER_TO_BANKS, {
        company_id: id,
      });
      if (response?.data?.result?.status_code == 200) {
        setTransferToBanks(response?.data?.result?.data);
      } else {
        setTransferToBanks([]);
      }
    } catch (err) {
      console.error("Failed to fetch substores:", err);
    }
  };
  const getSubStoresData = async () => {
    try {
      const res = await getSubstores(storeId);
      const allStores = res?.response || [];

      // ✅ Only keep active stores
      const activeStores = allStores.filter((store) => store?.active && !store?.is_warehouse);

      setSubStores(activeStores);
    } catch (err) {
      console.error("Failed to fetch substores:", err);
    }
  };

  const fetchBalance = async (selectedType, storeData = null) => {
    if (!selectedType) return;

    try {
      setBalanceLoading(true);

      const payload = {
        from_type: selectedType == "is_cash"
            ? "Cash"
            :  selectedType == "is_bank"
              ? "Bank"
              : selectedType,
      };

      if (selected === "Cash to Bank") {
        // Cash → Bank
        payload.store_ids = fromStore || [masterStore?.store_id || storeId];
      } else if (selected === "Branch") {
        // Branch transfers can be substore → master or master → substore
        if (storeData?.length < 1) return;
        payload.store_ids = storeData;
      }

      const res = await getTransferBalance(payload);
      if (res?.status_code === 200) {
        const balanceAmt = parseFloat(res?.balance || 0);
        setBalance(balanceAmt);
        if (fromStore?.length > 1) {
          setAmount(balanceAmt);
        }
      } else {
        setBalance(null);
        message.error(t("Failed to fetch balance"));
      }
    } catch (err) {
      console.error(err);
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleChange = (event, newValue) => {
    if (newValue !== null) {
      setSelected(newValue);
      setFromType("");
      setToType("");
      setAmount("");
      setRemarks("");
      setBalance(null);
      setToStore(null);
      setFromStore(null);
      setBalanceLoading(false);
    }
  };

  // Fetch transactions
  const fetchTransactions = async (start_date, end_date, limit, offset) => {
    try {
      // setLoading(true);
      const res = await getMoneyTransfers({
        is_branch: selected === "Cash to Bank" ? false : true,
        store_id: storeId,
        start_date,
        end_date,
        limit,
        offset,
      });

      if (res?.result?.status_code === 200) {
        setTransactions(res?.result?.response || []);
      } else {
        message.error(t("Failed to fetch transactions"));
      }
    } catch (err) {
      console.error(err);
      message.error(t("Failed to fetch transactions"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(startDate, endDate, 10, 0);
  }, [selected]);

  const resetForm = () => {
    setFromType("");
    setToType("");
    setAmount("");
    setRemarks("");
    setFromStore(null);
    setToStore(null);
    setBalance(null);
    setBalanceLoading(false);
  };

  // Save new transfer
  const handleSave = async () => {
    if (!fromType || !toType || !amount) {
      toast.warning(t("Please fill all required fields"));
      return;
    }
    if (selected !== "Branch" && fromType === toType) {
      toast.warning(t("From and To cannot be the same"));
      return;
    }

    const enteredAmount = parseFloat(amount);
    if (isNaN(enteredAmount) || enteredAmount <= 0) {
      toast.error(t("Amount must be greater than zero"));
      return;
    }

    if (balance !== null && balance <= 0) {
      toast.error(t("You don't have enough funds for this transfer"));
      return;
    }

    if (balance !== null && enteredAmount > balance) {
      toast.error(t("Entered amount exceeds available balance"));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        amount: Number(amount),
        remarks: remarks,
        from_type: fromType == "is_cash" ? "Cash" : fromType == "is_bank" ? "Bank" : fromType,
        to_type: toType == "is_cash" ? "Cash" : toType == "is_bank" ? "Bank" : toType,
        is_branch: selected === "Cash to Bank" ? false : true,
      };

      if (selected === "Branch") {
        //Branch → Branch
        payload.store_ids = fromStore; // From Store
        payload.to_store_id = toStore?.store_id; // To Store
      } else if (selected === "Cash to Bank") {
        payload.store_ids = fromStore || [masterStore?.store_id || storeId];
      }

      const res = await createMoneyTransfer(payload);

      if (res?.result?.status_code === 200) {
        toast.success(t("Money transfer created successfully"));
        resetForm();
        setSelectedRange("thisMonth");
        setStartDate(startOfMonth);
        setEndDate(formatToday);
        fetchTransactions(startOfMonth, formatToday, 10, 0);
      } else {
        message.error(t("Failed to save transfer"));
      }
    } catch (err) {
      console.error(err);
      message.error(t("Error saving transfer"));
    } finally {
      setSaving(false);
    }
  };

  // 🔹 Edit record
  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditModalOpen(true);
  };

  // 🔹 Refresh after edit
  const handleEditUpdate = () => {
    fetchTransactions(startDate, endDate, 10, 0);
  };

  // 🔹 Delete record
  const handleDelete = async (record) => {
    try {
      const payload = {
        record_id: record.id,
        from_type: record.from_type,
        to_type: record.to_type,
        amount: record.amount,
        remarks: record.remarks,
        is_delete: true,
      };

      const res = await editOrDeleteMoneyTransfer(payload);
      if (res?.result?.status_code === 200) {
        toast.success(t("Record deleted successfully"));
        fetchTransactions(startDate, endDate, 10, 0);
      } else {
        message.error(t("Failed to delete record"));
      }
    } catch (err) {
      message.error(t("Error deleting record"));
    }
  };

  const getToOptions = () => {
    if (selected === "Branch") {
      // Allow same type transfers for branch-to-branch
      if (fromType === "Cash") return ["Cash", "Bank"];
      if (fromType === "Bank") return ["Bank", "Cash"];
    } else {
      // For Cash-to-Bank, only opposite transfers allowed
      if (fromType === "Cash") return ["Bank"];
      if (fromType === "Bank") return ["Cash"];
    }
    return ["Cash", "Bank"];
  };

  const isSaveDisabled = !fromType || !toType || !amount || !remarks;
  const isCancelDisabled = !fromType && !toType && !amount && !remarks;

  const isInFromStore = (id) =>
    Array.isArray(fromStore) && fromStore.includes(id);

  return (
    <div className="flex w-full flex-col overflow-y-auto bg-[#F6F7F9] pb-6 min-h-screen">
      <SetHeading value={t("Money Transfer")} />

      <div className="px-3 py-4 sm:px-6 sm:py-6">
        <div className="mb-4 mt-2 flex w-full justify-center px-2 sm:mt-4 sm:px-4">
          <div className="w-0 md:w-4" />
          <ToggleButtonGroup
            value={selected}
            exclusive
            onChange={handleChange}
            sx={{
              borderRadius: "9999px", // fully rounded
              border: "1px solid #d1d5db", // tailwind's gray-300
              backgroundColor: "#fff",
              p: "4px",
              width: { xs: "100%", md: "auto" },
              justifyContent: { xs: "center", md: "center" },
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9999px",
                textTransform: "none",
                padding: "6px 16px",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#6b7280", // gray-500
                transition: "all 0.3s ease",
                fontFamily: i18n.language == "ar" ? "Cairo" : "sans-serif",
              },
              "& .Mui-selected": {
                backgroundColor: "#38bdf8 !important", // sky-400
                color: "#fff !important",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              },
            }}
          >
            <ToggleButton value="Cash to Bank">
              {t("Payment Mode Transfer")}
            </ToggleButton>
            <ToggleButton value="Branch">
              {t("Inter-Branch Transfer")}
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        {/* 🔹 Form Card */}
        {/* 🔹 Form Card */}
        <div className="mb-6 rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          {selected === "Branch" ? (
            //  BRANCH TO BRANCH FORM
            <div className="grid grid-cols-1 gap-6">
              {/* Store selection row */}
              <div className="flex gap-6 flex-wrap md:flex-nowrap">
                {/* From Store */}
                <div className="w-full md:w-1/2">
                  <label className="block text-sm mb-1 font-medium">
                    {t("Transfer From Branch")}
                  </label>
                
                  <MultiSelectBox
                    // label={t("Product Groups")}
                    name="store"
                    value={fromStore}
                    onChange={(event) => {
                      const value = event.target.value;
                      setToStore(null);
                      setFromType('');
                      setToType("");
                      setFromStore(value);
                      setBalance(null);
                      setAmount('')
                      getTransferFromBank(value);
                      // if (value?.length > 0 && fromType) {
                      //   fetchBalance(fromType, value);
                      // }
                    }}
                    placeholder={t("Select Branch")}
                    // defaultValue={masterStore?.store_id}
                    options={[
                      {
                        value: masterStore?.store_id,
                        label:
                          i18n.language === "ar"
                            ? masterStore?.store_name_ar
                            : masterStore?.store_name,
                      },
                      ...subStores?.map((item) => ({
                        value: item?.store_id,
                        label:
                          i18n.language === "ar"
                            ? item.store_name_ar
                            : item.store_name,
                      })),
                    ]}
                    // disabled={!isProductSelected}
                  />
                </div>

                {/* To Store */}
                <div className="w-full md:w-1/2">
                  <label className="block text-sm mb-1 font-medium">
                    {t("Transfer To Branch")}
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder={t("Select Branch")}
                    className="h-10"
                    showSearch
                    value={toStore?.store_id || null}
                    onChange={(value) => {
                      const selected =
                        value === masterStore?.store_id
                          ? masterStore
                          : subStores.find((store) => store.store_id === value);

                      // 🧠 Swap logic (IDs version)
                      if (isInFromStore(selected?.store_id)) {
                        setFromStore((prev) =>
                          prev.map((id) =>
                            id === selected.store_id ? toStore?.store_id : id,
                          ),
                        );
                        setToStore(selected);
                      } else {
                        setToStore(selected);
                      }
                      getTransferToBank(selected?.store_id)
                    }}
                  >
                    {/* Master Store */}
                    <Option
                      key={masterStore?.store_id}
                      value={masterStore?.store_id}
                      disabled={isInFromStore(masterStore?.store_id)}
                    >
                      {i18n.language === "ar"
                        ? masterStore?.store_name_ar
                        : masterStore?.store_name}
                    </Option>

                    {/* Sub Stores */}
                    {subStores?.map((store) => (
                      <Option
                        key={store.store_id}
                        value={store.store_id}
                        disabled={isInFromStore(store.store_id)}
                      >
                        {i18n.language === "ar"
                          ? store.store_name_ar
                          : store.store_name}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* From/To Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* From Type */}
                <div>
                  <label className="block text-sm mb-1 font-medium">
                    {t("From Payment Mode")}
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder={t("Select Payment Mode")}
                    className="h-10"
                    value={fromType}
                    onChange={(value) => {
                      setFromType(value);
                      setToType("");
                      setBalance(null);

                      if (fromStore) {
                        fetchBalance(value, fromStore);
                      }
                    }}
                    // onChange={(value) => {
                    //     setFromType(value);
                    //     setToType("");
                    // }}
                  >
                    {
  //                   fromStore?.length > 1 ? (
  //   <>
  //     <Option value="Cash">{t("Cash")}</Option>
  //     <Option value="Bank">{t("Bank")}</Option>
  //   </>
  // ) : (
    transferFromBanks?.map((bank) => (
      <Option key={bank.id} value={bank.id}>
        {t(bank.name)}
      </Option>
    ))
  // )
  }
                  </Select>
                </div>

                {/* To Type */}
                <div>
                  <label className="block text-sm mb-1 font-medium">
                    {t("To Payment Mode")}
                  </label>
                  <Select
                  style={{ width: "100%" }}
                  placeholder={t("Select Payment Mode")}
                  value={toType}
                  onChange={(value) => setToType(value)}
                  disabled={!fromType}
                >
                  {
  //                 (fromStore?.length > 1 || toStore?.store_id != masterStore?.store_id) ? (
  //   getToOptions().map((opt) => (
  //     <Option key={opt} value={opt}>
  //       {t(opt)}
  //     </Option>
  //   ))
  // ) : (
    transferToBanks
      // ?.filter((bank) => bank.id !== fromType)
      .map((bank) => (
        <Option key={bank.id} value={bank.id}>
          {t(bank.name)}
        </Option>
      ))
  // )
  }
                </Select>
                </div>
              </div>

              {/* Amount and Remarks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm mb-1 font-medium">
                    {t("Amount")}
                  </label>
                  <Input
                    type="text"
                    placeholder={t("Enter Amount")}
                    value={amount}
                    readOnly={fromStore?.length > 1}
                    disabled={fromStore?.length > 1 && fromType != '' && balance != null}
                    onChange={(e) => {
                      const value = e.target.value;
                      const regex = /^(?!0\d)(\d+(\.\d{0,2})?)?$/;
                      if (value === "" || regex.test(value)) {
                        if (value.startsWith(".") || value.startsWith("-"))
                          return;
                        setAmount(value);
                      }
                    }}
                    inputMode="decimal"
                    maxLength={25}
                    className="h-10"
                  />
                  {balanceLoading ? (
                    <p className="text-sm text-gray-500 mt-1">
                      {t("Fetching balance...")}
                    </p>
                  ) : balance !== null ? (
                    <p
                      className={`text-sm italic mt-1 ${
                        balance <= 0
                          ? "text-red font-medium"
                          : "text-green-600 font-medium"
                      }`}
                    >
                      {t("Available Balance")}: {balance.toFixed(2)}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className="block text-sm mb-1 font-medium">
                    {t("Remarks")}
                  </label>
                  <Input.TextArea
                    rows={2}
                    placeholder={t("Enter Remarks")}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-row justify-end gap-3 mt-6">
                <button
                  onClick={resetForm}
                  disabled={isCancelDisabled}
                  className={`px-5 py-2 text-sm font-semibold rounded-lg border transition-all ${
                    isCancelDisabled
                      ? "bg-white text-primaryBlue border-primaryBlue opacity-60 cursor-not-allowed"
                      : "bg-white text-primaryBlue border-primaryBlue hover:bg-red-600 "
                  }`}
                >
                  {t("Cancel")}
                </button>

                <button
                  onClick={handleSave}
                  disabled={isSaveDisabled || saving}
                  className={`px-5 py-2 text-sm font-semibold rounded-lg border transition-all ${
                    isSaveDisabled || saving
                      ? "bg-primaryBlue text-white border-primaryBlue opacity-60 cursor-not-allowed"
                      : "bg-primaryBlue text-white border-primaryBlue hover:bg-red-600"
                  }`}
                >
                  {saving ? t("Saving...") : t("Save")}
                </button>
              </div>
            </div>
          ) : (
            //  CASH TO BANK FORM
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {/* From */}
              <div>
                <label className="block text-sm mb-1 font-medium">
                  {t("From Payment Mode")}
                </label>
                <Select
                  style={{ width: "100%" }}
                  placeholder={t("Select Payment Mode")}
                  value={fromType}
                  // onChange={(value) => {
                  //     setFromType(value);
                  //     setToType("");
                  // }}
                  onChange={(value) => {
                    setFromType(value);
                    setToType("");
                    setBalance(null);
                    fetchBalance(value, fromStore || masterStore);
                  }}
                  className="h-10"
                >
                  {transferFromBanks?.map((bank) => (
                    <Option key={bank.id} value={bank.id}>
                      {t(bank.name)}
                    </Option>
                  ))}
                </Select>
              </div>

              {/* To */}
              <div>
                <label className="block text-sm mb-1 font-medium">
                  {t("To Payment Mode")}
                </label>
                <Select
                  style={{ width: "100%" }}
                  placeholder={t("Select Payment Mode")}
                  value={toType}
                  onChange={(value) => setToType(value)}
                  disabled={!fromType}
                  className="h-10"
                >
                  {transferFromBanks
                    ?.filter((bank) => bank.id !== fromType)
                    .map((bank) => (
                      <Option key={bank.id} value={bank.id}>
                        {t(bank.name)}
                      </Option>
                    ))}
                </Select>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm mb-1 font-medium">
                  {t("Amount")}
                </label>
                <Input
                  type="text"
                  placeholder={t("Enter Amount")}
                  value={amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    const regex = /^(?!0\d)(\d+(\.\d{0,2})?)?$/;
                    if (value === "" || regex.test(value)) {
                      if (value.startsWith(".") || value.startsWith("-"))
                        return;
                      setAmount(value);
                    }
                  }}
                  inputMode="decimal"
                  maxLength={11}
                  className="h-10"
                />
                {balanceLoading ? (
                  <p className="text-sm text-gray-500 mt-1">
                    {t("Fetching balance...")}
                  </p>
                ) : balance !== null ? (
                  <p
                    className={`text-sm italic mt-1 ${
                      balance <= 0
                        ? "text-red font-medium"
                        : "text-green-600 font-medium"
                    }`}
                  >
                    {t("Available Balance")}: {balance.toFixed(2)}
                  </p>
                ) : null}
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-sm mb-1 font-medium">
                  {t("Remarks")}
                </label>
                <Input.TextArea
                  rows={2}
                  placeholder={t("Enter Remarks")}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>

              {/* Buttons */}
              <div className="col-span-full mt-6 flex gap-3 flex-row sm:justify-end">
                <button
                  onClick={resetForm}
                  disabled={isCancelDisabled}
                  className={`w-full sm:w-auto px-5 py-2 text-sm font-semibold rounded-lg border transition-all ${
                    isCancelDisabled
                      ? "bg-white text-primaryBlue border-primaryBlue opacity-60 cursor-not-allowed"
                      : "bg-white text-primaryBlue border-primaryBlue hover:bg-red-600 "
                  }`}
                >
                  {t("Cancel")}
                </button>

                <button
                  onClick={handleSave}
                  disabled={isSaveDisabled || saving}
                  className={`w-full sm:w-auto px-5 py-2 text-sm font-semibold rounded-lg border transition-all ${
                    isSaveDisabled || saving
                      ? "bg-primaryBlue text-white border-primaryBlue opacity-60 cursor-not-allowed"
                      : "bg-primaryBlue text-white border-primaryBlue hover:bg-red-600"
                  }`}
                >
                  {saving ? t("Saving...") : t("Save")}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 🔹 Transactions Table */}
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-semibold">{t("Transactions")}</h3>
            <div className="w-full sm:w-auto">
              <DateFilter
                setSelectedRange={setSelectedRange}
                selectedRange={selectedRange}
                SubmitFilter={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                  setLoading(true);
                  fetchTransactions(start, end, 10, 0);
                }}
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-10">
              <LoadingSpinner />
            </div>
          ) : (
            <MoneyTransferTable
              additionalData={transactions}
              onEdit={handleEdit}
              onDelete={handleDelete}
              pagination={(values) => {
                setCurrentLimit(values.limit);
                setCurrentOffset(values.offset);
                fetchTransactions(
                  startDate,
                  endDate,
                  values.limit,
                  values.offset,
                );
              }}
              selected={selected}
            />
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <EditMoneyModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        record={editingRecord}
        onUpdate={handleEditUpdate}
      />
    </div>
  );
};

export default MoneyTransfer;
